import { Inject, Injectable } from '@angular/core';
import {
  BillableItem,
  BillingService,
  Currency,
  Discount,
  DiscountService,
  DiscountType,
  Frequency,
  ListBillableItemsRequestFilters,
  ProductPricing,
  WholesalePricingService,
} from '@galaxy/billing';
import {
  FREE_SUBSCRIPTION_ID,
  PartnerTrialService,
  SubscriptionTierInterface,
  SubscriptionTiersService,
  VBP2024_VERSION,
} from '@galaxy/partner';
import { TranslateService } from '@ngx-translate/core';
import { CampaignQuotaCategory, Period, QuotaService, SenderType } from '@vendasta/campaigns';
import moment from 'moment';
import { combineLatest, forkJoin, Observable, of, startWith } from 'rxjs';
import { catchError, map, publishReplay, refCount, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AppConfigService } from '../../app-config.service';
import { PartnerService } from '../../core/partner.service';
import { ProductSummary } from '../../core/product-summary/product-summary';
import { ProductSummaryService } from '../../core/product-summary/product-summary.service';
import { ProductService } from '../../core/product.service';
import { Product } from '../../core/product/product';
import { toUTCTime } from '../../core/utils';
import { YeswareAccountsApiService } from '@vendasta/vendasta-yesware-bridge';
import { GetAccountRequestInterface } from '@vendasta/vendasta-yesware-bridge/lib/_internal/interfaces';
import { AccessService, StubAccessService, Feature } from '../../core/access';

const SNAPSHOT_SKU = 'ST';
const TEAM_MEMBER_SEATS_SKU = 'sa-team-member-seats';

export interface SubscriptionLimit {
  showLimit: boolean;
  limitMax: number;
  currentValue: number;
  exceededLimitValue: number;
  limitTitle?: string;
  limitImageUrl?: string;
}

export interface SubscriptionCardData {
  skus: string[];
  showCard: boolean;
  upgradeCTA: string;
  title: string;
  subtitle: string;
  hasCurrentValue: boolean;
  learnMoreUrl?: string;
  maxLimit?: number;
  resetDate?: Date;
  subscriptionLimits: SubscriptionLimit[];
  loading?: boolean;
  exceedUnit: string;
  nextTierId: string;
  nextTierLimit: string;
  type: string;
}

export interface TierInfo {
  id: string;
  pricingPlanId: string;
  tierVersion: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  onboardingPrice: number;
  monthlyAddonPrice: number;
  yearlyAddonPrice: number;
  enabledFeatures: string[];
  teamMembers: number;
  emailsPerMonth: number;
  snapshotLimit: number;
  expressProductLimit: number;
  newSeatCost: number;
  wholesaleDiscount: number;
  rank: number;
  currency: string;
}

export const defaultSubscriptionCardData = ['SNAPSHOT', 'TOOLKIT', 'EMAIL', 'AUTOMATION', 'SEATS'].map(
  (cardNameShortHand) => {
    return {
      showCard: true,
      title: `SUBSCRIPTIONS.${cardNameShortHand}_TITLE`,
      subtitle: '',
      hasCurrentValue: false,
      loading: true,
    } as SubscriptionCardData;
  },
);

@Injectable()
export class ManageSubscriptionsService {
  env: string;
  billingFrequency$: Observable<Frequency>;
  currentTierId$: Observable<string>;
  currentTier$: Observable<SubscriptionTierInterface>;
  subscriptionPricing$: Observable<ProductPricing>;
  isFreeTier$: Observable<boolean>;
  cardData$: Observable<SubscriptionCardData[]> = of(defaultSubscriptionCardData);
  renewDate$: Observable<Date>;
  partnerSinceDate$: Observable<Date>;
  partnerCurrency$: Observable<Currency>;
  subscriptionPrice$: Observable<number>;
  isOnFreeTrial$: Observable<boolean>;
  freeTrialEndDate$: Observable<Date>;

  tiers$: Observable<TierInfo[]>;
  freeTier$: Observable<TierInfo[]>;

  private pricingPlans$: Observable<{ [sku: string]: ProductPricing }>;
  private productSummaryMap$: Observable<{ [sku: string]: ProductSummary }>;
  private products$: Observable<Product[]>;
  private emailDetails$: Observable<SubscriptionLimit>;
  private seatDetails$: Observable<SubscriptionLimit>;
  nextSubscriptionTier$: Observable<SubscriptionTierInterface>;
  mostSimilarSubscriptionTier$: Observable<SubscriptionTierInterface>;
  private nextSubscriptionUpgradeInfo$: Observable<TierInfo>;
  private productDetailsMap$: Observable<{ [sku: string]: SubscriptionLimit }>;
  private seatDiscounts$: Observable<SubscriptionLimit>;
  private skus$: Observable<string[]>;
  private toolkitSKUs$: Observable<string[]>;
  private discounts$: Observable<Discount[]>;
  private yeswareSeats$: Observable<SubscriptionLimit>;

  constructor(
    private readonly partnerService: PartnerService,
    private readonly subscriptionTiersService: SubscriptionTiersService,
    private readonly billingService: BillingService,
    private readonly productSummaryService: ProductSummaryService,
    private readonly appConfigService: AppConfigService,
    private readonly productService: ProductService,
    private readonly discountService: DiscountService,
    private readonly wholesalePricingService: WholesalePricingService,
    private readonly partnerTrialService: PartnerTrialService,
    private readonly emailQuotaService: QuotaService,
    private readonly yeswareService: YeswareAccountsApiService,
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private readonly translate: TranslateService,
    @Inject(StubAccessService) private accessService: AccessService,
  ) {
    this.env = this.getEnvironment();
    this.currentTier$ = this.partnerService.subscriptionTier$;
    this.currentTierId$ = this.currentTier$.pipe(
      map((t) => {
        return t.id;
      }),
    );

    this.toolkitSKUs$ = this.currentTier$.pipe(
      map((tier) => {
        const skus = [];
        const toolkitData = tier?.usageLimits?.toolkitData || [];
        toolkitData.forEach((tk) => skus.push(tk.sku));
        return skus;
      }),
    );

    this.skus$ = this.toolkitSKUs$.pipe(map((skus) => [TEAM_MEMBER_SEATS_SKU, SNAPSHOT_SKU, ...skus]));

    this.seatDetails$ = this.currentTier$.pipe(
      map((tier) => {
        return {
          showLimit: false,
          limitMax: tier.usageLimits?.teamMembers || 0,
          currentValue: -1,
          exceededLimitValue: 0,
          limitTitle: null,
          limitImageUrl: null,
        };
      }),
    );
    this.emailDetails$ = this.partnerId$.pipe(
      switchMap((pid) => {
        return this.emailQuotaService.getCampaignQuota(
          {
            type: SenderType.SENDER_TYPE_PARTNER,
            id: pid,
          },
          CampaignQuotaCategory.CAMPAIGN_QUOTA_TYPE_EMAIL,
          Period.PERIOD_MONTH,
        );
      }),
      map((resp) => {
        return {
          showLimit: true,
          limitMax: resp.limit || 0,
          currentValue: resp.count || 0,
          exceededLimitValue: 0,
          limitTitle: null,
          limitImageUrl: null,
        };
      }),
      catchError(() => {
        return of({
          showLimit: false,
          limitMax: -1,
          currentValue: -1,
          exceededLimitValue: 0,
          limitTitle: null,
          limitImageUrl: null,
        });
      }),
    );

    this.yeswareSeats$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.yeswareService.getAccount({ partnerId: partnerId } as GetAccountRequestInterface);
      }),
      map((resp) => {
        if (!resp?.account?.currentContract || !resp.account.currentContract?.totalSeats) {
          return {
            showLimit: false,
            limitMax: 0,
            currentValue: 0,
            exceededLimitValue: 0,
            limitTitle: null,
            limitImageUrl: null,
          };
        }
        const limitMax = resp.account.currentContract?.totalSeats || 0;
        let availableSeats = resp.account.currentContract?.availableSeats || 0;
        if (availableSeats < 0) {
          availableSeats = 0;
        }
        let currentValue = limitMax - availableSeats;
        if (currentValue < 0) {
          currentValue = 0;
        }
        return {
          showLimit: true,
          limitMax: limitMax,
          currentValue: currentValue,
          exceededLimitValue: 0,
          limitTitle: null,
          limitImageUrl: null,
        };
      }),
      catchError(() => {
        return of({
          showLimit: false,
          limitMax: 0,
          currentValue: 0,
          exceededLimitValue: 0,
          limitTitle: null,
          limitImageUrl: null,
        });
      }),
    );

    this.isFreeTier$ = this.currentTier$.pipe(
      map((st) => SubscriptionTiersService.isFree(st)),
      publishReplay(1),
      refCount(),
    );

    this.pricingPlans$ = combineLatest([this.partnerId$, this.currentTierId$, this.skus$]).pipe(
      switchMap(([partnerId, tierSKU, skus]) =>
        this.billingService.getMultiProductPricing(partnerId, [tierSKU, ...skus]),
      ),
      publishReplay(1),
      refCount(),
    );

    this.initTierInfo();

    this.nextSubscriptionTier$ = this.getNextHighestSubscriptionTier(this.currentTier$);
    this.mostSimilarSubscriptionTier$ = this.getMostSimilarSubscriptionTier(this.currentTier$);

    this.nextSubscriptionUpgradeInfo$ = combineLatest([this.tiers$, this.nextSubscriptionTier$]).pipe(
      map(([upgradeTierInfo, nextTier]) => {
        return upgradeTierInfo.find((t) => t.id === nextTier?.id);
      }),
    );

    const billingSubscription$ = combineLatest([this.partnerId$, this.currentTierId$]).pipe(
      switchMap(([partnerId, subscriptionSku]) => {
        const filters = new ListBillableItemsRequestFilters({
          skus: [subscriptionSku],
          merchantId: partnerId,
        });
        return this.billingService
          .listBillableItems('', filters, null, 1)
          .pipe(map((items) => items?.results[0] || ({} as BillableItem)));
      }),
      publishReplay(1),
      refCount(),
    );

    this.billingFrequency$ = billingSubscription$.pipe(map((item) => item.frequency));

    this.renewDate$ = billingSubscription$.pipe(map((item) => item.nextRenewal || new Date(0)));

    const subscriptionPrices$ = combineLatest([this.partnerId$, this.currentTierId$]).pipe(
      switchMap(([partnerId, subscriptionSku]) => {
        return this.wholesalePricingService.getMultiWholesalePrices(partnerId, [subscriptionSku]);
      }),
    );

    this.subscriptionPricing$ = combineLatest([subscriptionPrices$, this.billingFrequency$]).pipe(
      map(([prices, frequency]) => {
        if (prices.length > 0 && frequency) {
          return prices[0].prices.find((p) => p.frequency === frequency);
        }
        return {} as ProductPricing;
      }),
      publishReplay(1),
      refCount(),
    );

    const purchaseCost$ = combineLatest([this.partnerId$, this.currentTierId$]).pipe(
      switchMap(([partnerId, subscriptionSku]) => {
        return this.wholesalePricingService.getPurchaseCost(partnerId, subscriptionSku, 1);
      }),
    );

    this.subscriptionPrice$ = purchaseCost$.pipe(
      map((purchaseCost) => {
        return purchaseCost.total;
      }),
      publishReplay(1),
      refCount(),
    );

    this.partnerSinceDate$ = this.appConfigService.config$.pipe(
      map((ac) => {
        return new Date(ac.partnerCreated * 1000);
      }),
    );

    this.partnerCurrency$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.billingService
          .getMultiContract([partnerId])
          .pipe(map((contracts) => contracts[partnerId].currency));
      }),
    );

    this.productSummaryMap$ = combineLatest([this.partnerId$, this.skus$]).pipe(
      tap(([partnerId, skus]) => this.loadProductSummaries(partnerId, skus)),
      switchMap(([, skus]) => this.getProductSummaryMap(skus)),
    );

    this.products$ = this.partnerId$.pipe(
      switchMap(() => {
        return this.productService.products.pipe(shareReplay(1));
      }),
    );

    this.discounts$ = combineLatest([this.partnerId$, this.skus$]).pipe(
      switchMap(([partnerId, skus]) => {
        const tomorrow = moment().utc().add(1, 'day').hour(0).minute(0).second(0).millisecond(0).toDate();
        return this.discountService.list(
          {
            merchantId: partnerId,
            expiry: tomorrow,
            skus: skus,
          },
          '',
          100,
        );
      }),
      map((resp) => resp.results.filter((d) => d.resetEachPeriod)),
    );

    this.productDetailsMap$ = this.getProductDetailsMap();

    this.seatDiscounts$ = this.productDetailsMap$.pipe(
      map((productDetailsMap) => productDetailsMap[TEAM_MEMBER_SEATS_SKU]),
    );

    this.cardData$ = this.getCardData().pipe(publishReplay(1), refCount());

    const freeTrial$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.partnerTrialService.getTrial(partnerId);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.isOnFreeTrial$ = freeTrial$.pipe(
      map((trial) => {
        const currentDate = new Date();
        // isActive checks if the current date is between start and end dates and cancel date is not set
        return trial.start <= currentDate && trial.end >= currentDate;
      }),
    );

    this.freeTrialEndDate$ = freeTrial$.pipe(
      map((trial) => {
        return trial.end;
      }),
    );
  }

  private initTierInfo(): void {
    this.tiers$ = this.choseSubscriptionTier(this.getCurrentSubscriptionTiers());
    this.freeTier$ = this.choseSubscriptionTier(this.getFreeSubscriptionTier());
  }

  private choseSubscriptionTier(
    selectedSubscriptionTier: Observable<SubscriptionTierInterface[]>,
  ): Observable<TierInfo[]> {
    let shouldHaveOnboarding = false;

    return combineLatest([selectedSubscriptionTier, this.partnerId$]).pipe(
      switchMap(([tiers, partnerId]) => {
        return forkJoin(
          tiers.map((tier) => {
            const tierID = tier.id;
            let requestSkus = [tierID];
            if (tier?.subscriptionUpdate?.oneTimeSubscriptionSkus?.length > 0) {
              requestSkus = requestSkus.concat(tier.subscriptionUpdate.oneTimeSubscriptionSkus);
            }
            if (tier?.subscriptionUpdate.monthlySubscriptionSkus?.length > 0) {
              requestSkus = requestSkus.concat(tier.subscriptionUpdate.monthlySubscriptionSkus);
            }

            return this.wholesalePricingService
              .getMultiWholesalePrices(partnerId, requestSkus, tier?.pricingPlanId)
              .pipe(
                map((pricing) => {
                  let onboardingPrice = 0;
                  if (tier?.subscriptionUpdate?.oneTimeSubscriptionSkus?.length > 0) {
                    pricing
                      .filter((p) => tier.subscriptionUpdate.oneTimeSubscriptionSkus.includes(p.sku))
                      .forEach((p) => {
                        shouldHaveOnboarding = true;
                        if (p.prices.length > 0) {
                          onboardingPrice += p.prices[0]?.pricingRules[0]?.price / 100;
                        }
                      });
                  }
                  let monthlyAddonPrice = 0;
                  let yearlyAddonPrice = 0;
                  if (tier?.subscriptionUpdate?.monthlySubscriptionSkus?.length > 0) {
                    pricing
                      .filter((p) => tier.subscriptionUpdate.monthlySubscriptionSkus.includes(p.sku))
                      .forEach((p) => {
                        if (p.prices.length > 0) {
                          const monthlyPrice = p.prices.filter((pr) => pr.frequency === 'MONTHLY');
                          monthlyAddonPrice += monthlyPrice[0]?.pricingRules[0]?.price / 100;

                          const yearlyPrice = p.prices.filter((pr) => pr.frequency === 'YEARLY');
                          yearlyAddonPrice += yearlyPrice[0]?.pricingRules[0]?.price / 100;
                        }
                      });
                  }
                  const subscriptionPricing = pricing.find((p) => p.sku === tierID);
                  let monthlyPrice =
                    subscriptionPricing?.prices.find((p) => p.frequency === Frequency.Monthly)?.pricingRules[0]?.price /
                    100;
                  let annualPrice =
                    subscriptionPricing?.prices.find((p) => p.frequency === Frequency.Yearly)?.pricingRules[0]?.price /
                      100 +
                    yearlyAddonPrice;
                  const snapshotLimit = tier?.usageLimits?.snapshots || 0;
                  const expressProductLimit = tier?.usageLimits?.toolkits || 0;
                  const currency = subscriptionPricing?.prices[0]?.currency;
                  const {
                    enabledFeatures,
                    usageLimits: {
                      teamMembers: teamMembers,
                      emailsPerMonth: emailsPerMonth,
                      newSeatCost: newSeatCost,
                      wholesaleDiscount: wholesaleDiscount,
                    },
                    id,
                    name,
                    rank,
                  } = tier;

                  if (!monthlyPrice) {
                    monthlyPrice = tier.usageLimits.monthlyFee / 100;
                  }
                  if (!annualPrice) {
                    annualPrice = tier.usageLimits.annualFee / 100;
                  }
                  if (shouldHaveOnboarding && onboardingPrice === 0) {
                    onboardingPrice = tier.usageLimits.onboardingFee / 100;
                  }

                  return {
                    id,
                    pricingPlanId: tier.pricingPlanId,
                    tierVersion: tier.tierVersion,
                    name,
                    monthlyPrice,
                    annualPrice: annualPrice,
                    onboardingPrice,
                    monthlyAddonPrice,
                    yearlyAddonPrice,
                    enabledFeatures,
                    teamMembers,
                    emailsPerMonth,
                    snapshotLimit,
                    expressProductLimit,
                    newSeatCost,
                    wholesaleDiscount,
                    rank,
                    currency,
                  } as TierInfo;
                }),
              );
          }),
        );
      }),
    );
  }

  loadProductSummaries(partnerId: string, skus: string[]): void {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    this.productSummaryService.loadProductSummaries(
      partnerId,
      toUTCTime(startDate),
      toUTCTime(endDate),
      null,
      skus.length,
      skus,
    );
  }

  private getCurrentSubscriptionTiers(): Observable<SubscriptionTierInterface[]> {
    return this.subscriptionTiersService.subscriptionTierList$.pipe(
      map((tiers) => {
        return tiers.filter((tier) => !tier.archived && tier.allowSelfUpgrade && tier.tierVersion === VBP2024_VERSION);
      }),
    );
  }

  private getFreeSubscriptionTier(): Observable<SubscriptionTierInterface[]> {
    return this.subscriptionTiersService.subscriptionTierList$.pipe(
      map((tiers) =>
        tiers.filter((tier) => {
          return tier.id === FREE_SUBSCRIPTION_ID;
        }),
      ),
    );
  }

  // Used to get the tier above their current one to promote upgrading
  private getNextHighestSubscriptionTier(
    tier$: Observable<SubscriptionTierInterface>,
  ): Observable<SubscriptionTierInterface> {
    return combineLatest([this.subscriptionTiersService.subscriptionTierList$, tier$]).pipe(
      map(([tiers, currentTier]) => {
        const availableTiersSorted = tiers
          .filter((t) => t.tierVersion === VBP2024_VERSION && t.allowSelfUpgrade && !t.archived)
          .sort((a: SubscriptionTierInterface, b: SubscriptionTierInterface) => a.rank - b.rank);

        // Return first tier with a higher rank than current tier OR nothing
        return availableTiersSorted.filter((t) => Math.floor(t.rank / 100) > Math.floor(currentTier.rank / 100))[0];
      }),
    );
  }

  // Used to recommend a currently supported tier for those on a legacy subscription
  private getMostSimilarSubscriptionTier(
    tier$: Observable<SubscriptionTierInterface>,
  ): Observable<SubscriptionTierInterface> {
    return combineLatest([this.subscriptionTiersService.subscriptionTierList$, tier$]).pipe(
      map(([tiers, currentTier]) => {
        const availableTiersSorted = tiers
          .filter((t) => t.tierVersion === VBP2024_VERSION && !t.archived)
          .sort((a: SubscriptionTierInterface, b: SubscriptionTierInterface) => a.rank - b.rank);
        // Return first tier with the same or higher rank than current tier OR return the highest tier we have
        return (
          availableTiersSorted.filter((t) => Math.floor(t.rank / 100) >= Math.floor(currentTier.rank / 100))[0] ||
          availableTiersSorted[availableTiersSorted.length - 1]
        );
      }),
    );
  }

  private getProductSummaryMap(skus: string[]): Observable<{ [sku: string]: ProductSummary }> {
    return this.productSummaryService.productSummaries$.pipe(
      map((productSummaries) => {
        const productSummaryMap: { [sku: string]: ProductSummary } = {};
        skus.forEach((sku) => {
          const summary = productSummaries.find((ps) => ps.sku === sku);
          if (summary) {
            productSummaryMap[sku] = summary;
          }
        });
        return productSummaryMap;
      }),
    );
  }

  private getProductDetailsMap(): Observable<{ [sku: string]: SubscriptionLimit }> {
    return combineLatest([
      this.skus$,
      this.productSummaryMap$,
      this.pricingPlans$,
      this.products$,
      this.discounts$,
    ]).pipe(
      map(([skus, productSummaryMap, pricingPlans, products, discounts]) => {
        const productDetailsMap: { [sku: string]: SubscriptionLimit } = {};
        skus.forEach((sku) => {
          const productSummary: ProductSummary = productSummaryMap[sku];
          const pricingPlan: ProductPricing = pricingPlans[sku];
          const product: Product = products.find((p) => sku.includes(p.billing_id));

          const limitTitle = product?.name;
          const limitImageUrl = product?.icon_url || null;
          let limitMax = pricingPlan.pricingRules.find((pr) => pr.price === 0)?.maxUnits || 0;
          discounts
            .filter((discount) => discount.SKU === sku && discount.discountType === DiscountType.FIXED_NUMBER_OF_UNITS)
            .forEach((discount) => {
              limitMax += discount.amount;
            });
          const totalQuantity = productSummary?.totalQuantity || 0;
          let currentValue = totalQuantity;
          if (limitMax > 0 && currentValue > limitMax) {
            currentValue = limitMax;
          }
          productDetailsMap[sku] = {
            showLimit: limitMax !== 0 && (!!limitTitle || !!limitImageUrl),
            limitMax: limitMax,
            currentValue: currentValue,
            exceededLimitValue: totalQuantity > limitMax ? totalQuantity - limitMax : 0,
            limitTitle: limitTitle,
            limitImageUrl: limitImageUrl,
          };
        });
        return productDetailsMap;
      }),
      publishReplay(1),
      refCount(),
    );
  }

  private getCardData(): Observable<SubscriptionCardData[]> {
    const defaultCard = (type: string): SubscriptionCardData => ({
      skus: [],
      showCard: false,
      upgradeCTA: '',
      title: `SUBSCRIPTIONS.${type}_TITLE`,
      subtitle: '',
      hasCurrentValue: false,
      subscriptionLimits: [],
      exceedUnit: '',
      nextTierId: '',
      nextTierLimit: '',
      type,
      loading: true,
    });

    const snapshotCardData$ = combineLatest([
      this.productDetailsMap$,
      this.nextSubscriptionTier$,
      this.nextSubscriptionUpgradeInfo$,
    ]).pipe(
      map(([productDetailsMap, nextTier, nextTierUpgradeInfo]) => {
        const snapshotDetails = productDetailsMap[SNAPSHOT_SKU];
        const now = new Date();
        const resetDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));

        const snapShotSubtitle =
          snapshotDetails.limitMax >= 0
            ? `SUBSCRIPTIONS.SNAPSHOT_SUBTITLE${snapshotDetails.limitMax > 1 ? '_PLURAL' : ''}`
            : 'SUBSCRIPTIONS.SNAPSHOT_SUBTITLE_UNLIMITED';
        const showSnapshotCTA = this.showCTA(snapshotDetails);

        return <SubscriptionCardData>{
          skus: [SNAPSHOT_SKU],
          showCard: snapshotDetails.limitMax !== 0,
          upgradeCTA: showSnapshotCTA ? 'SUBSCRIPTIONS.SNAPSHOT_UPGRADE_CTA' : '',
          title: 'SUBSCRIPTIONS.SNAPSHOT_TITLE',
          subtitle: snapShotSubtitle,
          maxLimit: snapshotDetails.limitMax,
          hasCurrentValue: true,
          resetDate,
          exceedUnit: 'REPORT',
          subscriptionLimits: [<SubscriptionLimit>{ ...snapshotDetails, showLimit: true, hasCurrentValue: true }],
          nextTierId: nextTier?.id,
          nextTierLimit:
            nextTierUpgradeInfo?.snapshotLimit >= 0
              ? nextTierUpgradeInfo.snapshotLimit.toString()
              : 'SUBSCRIPTIONS.UNLIMITED',
          type: 'Snapshot Reports',
        };
      }),
      catchError((error) => {
        console.error('Error loading snapshot card data:', error);
        return of(defaultCard('SNAPSHOT'));
      }),
    );

    const toolkitCardData$ = combineLatest([
      this.toolkitSKUs$,
      this.productDetailsMap$,
      this.nextSubscriptionTier$,
      this.nextSubscriptionUpgradeInfo$,
    ]).pipe(
      map(([skus, productDetailsMap, nextTier, nextTierUpgradeInfo]) => {
        const toolkitLimits: SubscriptionLimit[] = skus.map((sku) => productDetailsMap[sku]);
        const toolKitLimitMax = this.getToolKitLimitMax(toolkitLimits);
        const toolKitSubtitle =
          toolKitLimitMax >= 0
            ? `SUBSCRIPTIONS.EXPRESS_PRODUCT_SUBTITLE${toolKitLimitMax > 1 ? '_PLURAL' : ''}`
            : 'SUBSCRIPTIONS.EXPRESS_PRODUCT_SUBTITLE_UNLIMITED';
        return <SubscriptionCardData>{
          skus: skus,
          showCard: toolKitLimitMax !== 0,
          upgradeCTA: '',
          title: 'SUBSCRIPTIONS.EXPRESS_PRODUCT_TITLE',
          subtitle: toolKitSubtitle,
          maxLimit: toolKitLimitMax,
          hasCurrentValue: true,
          exceedUnit: 'PRODUCT',
          subscriptionLimits: toolkitLimits,
          nextTierId: nextTier?.id,
          nextTierLimit:
            nextTierUpgradeInfo?.expressProductLimit >= 0
              ? nextTierUpgradeInfo.expressProductLimit.toString()
              : 'SUBSCRIPTIONS.UNLIMITED',
          type: 'Express Products',
        };
      }),
      catchError((error) => {
        console.error('Error loading toolkit card data:', error);
        return of(defaultCard('EXPRESS_PRODUCT'));
      }),
    );

    const emailCardData$ = combineLatest([
      this.emailDetails$,
      this.nextSubscriptionTier$,
      this.nextSubscriptionUpgradeInfo$,
    ]).pipe(
      map(([emailDetails, nextTier, nextTierUpgradeInfo]) => {
        return <SubscriptionCardData>{
          skus: [],
          showCard: emailDetails.limitMax > 0,
          upgradeCTA: '',
          title: 'SUBSCRIPTIONS.EMAIL_TITLE',
          subtitle: this.translate.instant('SUBSCRIPTIONS.EMAIL_SUBTITLE', { maxLimit: emailDetails.limitMax }),
          maxLimit: emailDetails.limitMax,
          hasCurrentValue: true,
          exceedUnit: 'EMAIL',
          subscriptionLimits: [<SubscriptionLimit>{ ...emailDetails, showLimit: true, hasCurrentValue: true }],
          nextTierId: nextTier?.id,
          nextTierLimit:
            nextTierUpgradeInfo?.emailsPerMonth >= 0
              ? nextTierUpgradeInfo.emailsPerMonth.toString()
              : 'SUBSCRIPTIONS.UNLIMITED',
          type: 'Emails',
        };
      }),
      catchError((error) => {
        console.error('Error loading email card data:', error);
        return of(defaultCard('EMAIL'));
      }),
    );

    const seatCardData$ = combineLatest([
      this.seatDetails$,
      this.nextSubscriptionTier$,
      this.nextSubscriptionUpgradeInfo$,
      this.isFreeTier$,
      this.seatDiscounts$,
    ]).pipe(
      map(([seatDetails, nextTier, nextTierUpgradeInfo, isFreeTier, seatDiscounts]) => {
        const seatLimit = seatDiscounts?.limitMax ?? seatDetails?.limitMax;

        return <SubscriptionCardData>{
          skus: [],
          showCard: seatLimit !== 0 && !isFreeTier,
          upgradeCTA: '',
          title: 'SUBSCRIPTIONS.SEATS_TITLE',
          subtitle: `SUBSCRIPTIONS.SEATS_SUBTITLE${seatLimit > 1 ? '_PLURAL' : seatLimit < 0 ? '_UNLIMITED' : ''}`,
          maxLimit: seatLimit,
          hasCurrentValue: false,
          exceedUnit: 'SEAT',
          subscriptionLimits: [{ ...seatDetails }],
          nextTierId: nextTier?.id,
          nextTierLimit:
            nextTierUpgradeInfo?.teamMembers >= 0
              ? nextTierUpgradeInfo.teamMembers.toString()
              : 'SUBSCRIPTIONS.UNLIMITED',
          type: 'Seats',
        };
      }),
      catchError((error) => {
        console.error('Error loading seat card data:', error);
        return of(defaultCard('SEATS'));
      }),
    );

    const yeswareCardData$ = combineLatest([
      this.yeswareSeats$,
      this.accessService.hasAccessToFeature(Feature.myProducts),
      seatCardData$,
    ]).pipe(
      map(([yeswareSeats, hasAccess, seatData]) => {
        yeswareSeats.currentValue = yeswareSeats.currentValue || 0;

        return <SubscriptionCardData>{
          skus: [],
          showCard: hasAccess,
          upgradeCTA: '',
          title: 'SUBSCRIPTIONS.YESWARE_TITLE',
          subtitle: 'SUBSCRIPTIONS.YESWARE_SUBTITLE',
          maxLimit: seatData.maxLimit,
          hasCurrentValue: true,
          exceedUnit: 'SEAT',
          subscriptionLimits: [
            {
              ...yeswareSeats,
              limitMax: seatData.maxLimit,
              showLimit: true,
            },
          ],
          nextTierId: '',
          nextTierLimit: '',
          type: 'Yesware Seats',
        };
      }),
      catchError((error) => {
        console.error('Error loading yesware card data:', error);
        return of(defaultCard('YESWARE'));
      }),
    );

    // Use combineLatest with default values to ensure cards are returned even if some fail
    return combineLatest([
      snapshotCardData$.pipe(startWith(defaultCard('SNAPSHOT'))),
      toolkitCardData$.pipe(startWith(defaultCard('EXPRESS_PRODUCT'))),
      emailCardData$.pipe(startWith(defaultCard('EMAIL'))),
      seatCardData$.pipe(startWith(defaultCard('SEATS'))),
      yeswareCardData$.pipe(startWith(defaultCard('YESWARE'))),
    ]);
  }

  private showCTA(limits: SubscriptionLimit): boolean {
    return limits.currentValue / limits.limitMax >= 0.8 || limits.limitMax < 2;
  }

  private getToolKitLimitMax(subscriptionLimits: SubscriptionLimit[]): number {
    const limits = subscriptionLimits.map((sl) => sl.limitMax);
    let limitMax = limits.filter((n) => n > 0).sort()[0];
    if (!limitMax) {
      limitMax = limits.filter((n) => n === -1)[0] || 0;
    }
    return limitMax;
  }

  private getEnvironment(): string {
    return this.appConfigService.config.environment !== 'prod' ? 'demo' : 'prod';
  }

  getProductDetailsMap$ = (): Observable<{ [sku: string]: SubscriptionLimit }> => {
    return this.productDetailsMap$;
  };

  getToolkitSKUs$ = (): Observable<string[]> => {
    return this.toolkitSKUs$;
  };

  public getProductDetails(sku: string): Observable<SubscriptionLimit> {
    return this.productDetailsMap$.pipe(map((resp) => resp[sku]));
  }
}
